import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import { Layout } from '../components/layout/Layout';

// Static blog posts data
const blogPosts = [
    {
        id: 'streetwear-trends-2025',
        title: 'Top Streetwear Trends to Watch in 2025',
        excerpt: 'From oversized silhouettes to bold graphics, discover the hottest streetwear trends that are defining the fashion landscape this year.',
        category: 'Trends',
        date: '2025-02-20',
        readTime: '5 min read',
        image: 'https://images.unsplash.com/photo-1523398002811-999ca8dec234?w=800&q=80',
        featured: true
    },
    {
        id: 'how-to-style-oversized-tees',
        title: 'How to Style Oversized Tees Like a Pro',
        excerpt: 'Master the art of styling oversized t-shirts with our comprehensive guide. From layering techniques to accessorizing tips.',
        category: 'Style Guide',
        date: '2025-02-15',
        readTime: '4 min read',
        image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=800&q=80',
        featured: false
    },
    {
        id: 'sustainable-streetwear',
        title: 'The Rise of Sustainable Streetwear',
        excerpt: 'How eco-conscious fashion is reshaping the streetwear industry. Learn about sustainable materials and ethical manufacturing.',
        category: 'Sustainability',
        date: '2025-02-10',
        readTime: '6 min read',
        image: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=800&q=80',
        featured: false
    },
    {
        id: 'hoodie-culture-history',
        title: 'The Cultural History of the Hoodie',
        excerpt: 'From athletic wear to fashion statement - explore how the humble hoodie became a symbol of street culture and self-expression.',
        category: 'Culture',
        date: '2025-02-05',
        readTime: '7 min read',
        image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=800&q=80',
        featured: false
    },
    {
        id: 'color-blocking-guide',
        title: 'Color Blocking: A Streetwear Essential',
        excerpt: 'Learn how to master color blocking in your streetwear outfits. Tips on combining bold colors for maximum impact.',
        category: 'Style Guide',
        date: '2025-01-28',
        readTime: '4 min read',
        image: 'https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?w=800&q=80',
        featured: false
    },
    {
        id: 'behind-the-brand',
        title: 'Behind the Brand: Our Story',
        excerpt: 'Discover the journey of YESSuhchill - from a small idea to a growing streetwear movement. Our passion, our vision, our chill.',
        category: 'Brand',
        date: '2025-01-20',
        readTime: '5 min read',
        image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
        featured: false
    }
];

const categories = ['All', 'Trends', 'Style Guide', 'Culture', 'Sustainability', 'Brand'];

const BlogCard = ({ post, index, featured = false }) => {
    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={featured ? 'lg:col-span-2' : ''}
        >
            <Link
                to={`/blog/${post.id}`}
                className={`group block bg-card border border-border hover:border-lime/50 transition-colors overflow-hidden ${
                    featured ? 'grid md:grid-cols-2' : ''
                }`}
                data-testid={`blog-post-${post.id}`}
            >
                {/* Image */}
                <div className={`relative overflow-hidden ${featured ? 'aspect-[16/10] md:aspect-auto' : 'aspect-[16/10]'}`}>
                    <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                        <span className="bg-lime text-black px-3 py-1 text-[10px] font-bold tracking-wider uppercase">
                            {post.category}
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div className={`p-5 ${featured ? 'md:p-8 flex flex-col justify-center' : ''}`}>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                        </span>
                    </div>
                    <h2 className={`font-heading mb-3 group-hover:text-lime transition-colors ${
                        featured ? 'text-2xl md:text-3xl' : 'text-xl'
                    }`}>
                        {post.title}
                    </h2>
                    <p className={`text-muted-foreground text-sm leading-relaxed ${featured ? 'mb-6' : 'mb-4'}`}>
                        {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-lime text-xs font-bold tracking-wider uppercase group-hover:gap-3 transition-all">
                        READ MORE <ArrowRight className="w-4 h-4" />
                    </span>
                </div>
            </Link>
        </motion.article>
    );
};

export const BlogPage = () => {
    const [activeCategory, setActiveCategory] = useState('All');

    const filteredPosts = activeCategory === 'All' 
        ? blogPosts 
        : blogPosts.filter(post => post.category === activeCategory);

    const featuredPost = filteredPosts.find(post => post.featured);
    const regularPosts = filteredPosts.filter(post => !post.featured);

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="blog-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    {/* Header */}
                    <div className="text-center mb-12 md:mb-16">
                        <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">
                            STORIES & STYLE
                        </span>
                        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl mb-4">
                            THE BLOG
                        </h1>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Style guides, trend reports, and stories from the streets. Stay informed, stay chill.
                        </p>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="blog-filters">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-4 py-2 text-xs font-semibold tracking-[0.1em] uppercase border transition-all ${
                                    activeCategory === category
                                        ? 'bg-lime text-black border-lime'
                                        : 'border-border text-muted-foreground hover:border-white hover:text-white'
                                }`}
                                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Blog Grid */}
                    {filteredPosts.length > 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Featured Post */}
                            {featuredPost && (
                                <BlogCard post={featuredPost} index={0} featured={true} />
                            )}
                            
                            {/* Regular Posts */}
                            {regularPosts.map((post, index) => (
                                <BlogCard key={post.id} post={post} index={index + 1} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground">No posts found in this category.</p>
                        </div>
                    )}

                    {/* Newsletter CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-16 md:mt-24 bg-card border border-border p-8 md:p-12 text-center"
                    >
                        <Tag className="w-10 h-10 mx-auto mb-4 text-lime" />
                        <h2 className="font-heading text-3xl md:text-4xl mb-4">NEVER MISS A POST</h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Subscribe to our newsletter for the latest style guides, trend reports, and exclusive content.
                        </p>
                        <Link
                            to="/#newsletter"
                            className="inline-block bg-lime text-black px-8 py-4 text-xs font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all"
                        >
                            SUBSCRIBE NOW
                        </Link>
                    </motion.div>
                </div>
            </div>
        </Layout>
    );
};

// Blog Detail Page Component
export const BlogDetailPage = () => {
    const postId = window.location.pathname.split('/').pop();
    const post = blogPosts.find(p => p.id === postId);

    if (!post) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="font-heading text-4xl mb-4">Post Not Found</h1>
                        <Link to="/blog" className="text-lime underline">Back to Blog</Link>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <article className="pt-32 md:pt-40 pb-16" data-testid="blog-detail-page">
                <div className="max-w-[900px] mx-auto px-4 md:px-8">
                    {/* Back Link */}
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-lime mb-8 transition-colors"
                    >
                        ← BACK TO BLOG
                    </Link>

                    {/* Header */}
                    <header className="mb-8">
                        <span className="bg-lime text-black px-3 py-1 text-[10px] font-bold tracking-wider uppercase inline-block mb-4">
                            {post.category}
                        </span>
                        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
                            {post.title}
                        </h1>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {post.readTime}
                            </span>
                        </div>
                    </header>

                    {/* Featured Image */}
                    <div className="aspect-[16/9] mb-10 overflow-hidden">
                        <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Content */}
                    <div className="prose prose-invert prose-lg max-w-none">
                        <p className="text-lg leading-relaxed text-muted-foreground mb-6">
                            {post.excerpt}
                        </p>
                        <p className="leading-relaxed mb-6">
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                        </p>
                        <h2 className="font-heading text-2xl mt-10 mb-4">THE DETAILS</h2>
                        <p className="leading-relaxed mb-6">
                            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                        </p>
                        <p className="leading-relaxed mb-6">
                            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
                        </p>
                        <blockquote className="border-l-4 border-lime pl-6 my-8 italic text-xl">
                            "Fashion is about dressing according to what's fashionable. Style is more about being yourself."
                        </blockquote>
                        <p className="leading-relaxed mb-6">
                            Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
                        </p>
                    </div>

                    {/* Share */}
                    <div className="mt-12 pt-8 border-t border-border">
                        <p className="text-sm text-muted-foreground mb-4 uppercase tracking-wider">Share this article</p>
                        <div className="flex gap-3">
                            <button className="px-4 py-2 border border-border text-xs uppercase tracking-wider hover:border-lime hover:text-lime transition-colors">
                                Twitter
                            </button>
                            <button className="px-4 py-2 border border-border text-xs uppercase tracking-wider hover:border-lime hover:text-lime transition-colors">
                                Facebook
                            </button>
                            <button className="px-4 py-2 border border-border text-xs uppercase tracking-wider hover:border-lime hover:text-lime transition-colors">
                                Copy Link
                            </button>
                        </div>
                    </div>

                    {/* Related Posts */}
                    <div className="mt-16">
                        <h3 className="font-heading text-2xl mb-6">MORE TO READ</h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            {blogPosts.filter(p => p.id !== post.id).slice(0, 2).map((relatedPost) => (
                                <Link
                                    key={relatedPost.id}
                                    to={`/blog/${relatedPost.id}`}
                                    className="group bg-card border border-border hover:border-lime/50 transition-colors overflow-hidden"
                                >
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            src={relatedPost.image}
                                            alt={relatedPost.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <h4 className="font-heading text-lg group-hover:text-lime transition-colors">
                                            {relatedPost.title}
                                        </h4>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </article>
        </Layout>
    );
};
